import { Providers } from "@tripian/model";
import { LIVN_DEFAULT_MARKUP_PERC, LIVN_SUPPLIER_MARKUP_PERC } from "../config/livnMarkup";

type LivnPrice = Providers.Livn.Price;
type LivnProductLike = Pick<
  Providers.Livn.ProductDetails,
  "usesNetRates" | "netRatesMarkupPerc" | "commissionPerc" | "supplier"
>;

/**
 * Livn Direct Connect products can supply net (non-commissionable) rates instead of
 * gross retail prices. Per Livn's certification guideline, the seller is responsible
 * for applying a markup before showing these prices to customers.
 *
 * See src/config/livnMarkup.ts for the resolution order and how to configure a
 * markup per supplier.
 */
export const getLivnMarkupPerc = (product?: LivnProductLike): number | undefined => {
  // A supplier can be flagged as net-rate on either the product or the supplier itself.
  const usesNetRates = product?.usesNetRates === true || product?.supplier?.usesNetRates === true;
  if (!usesNetRates) return undefined;

  const supplierId = product?.supplier?.id;

  // Net-rate products routinely report commissionPerc: 0 — there is no commission precisely
  // because the rate is net. Only a positive percentage counts as a configured markup, so a
  // zero must fall through to the default instead of silently disabling it.
  const candidates = [
    supplierId !== undefined ? LIVN_SUPPLIER_MARKUP_PERC[supplierId] : undefined,
    product?.netRatesMarkupPerc,
    product?.commissionPerc,
  ];
  const configured = candidates.find(v => typeof v === "number" && v > 0);

  return configured ?? LIVN_DEFAULT_MARKUP_PERC;
};

export const applyLivnMarkup = (price: LivnPrice, markupPerc?: number): LivnPrice => {
  if (!markupPerc) return price;
  return { ...price, amount: Math.round(price.amount * (1 + markupPerc / 100) * 100) / 100 };
};

export const displayLivnPrice = (price: LivnPrice, product?: LivnProductLike): LivnPrice =>
  applyLivnMarkup(price, getLivnMarkupPerc(product));

/**
 * Pass-through supplier fees that must always be shown at their original price. Livn flags
 * transfer fee lines as net-rate like every other line on a net-rate product, but its
 * certification requires them to reach the customer unmarked.
 */
const MARKUP_EXEMPT_LINE_TYPES = new Set(["TRANSFER"]);

export const displayLivnLineItemTotal = (
  lineItem: Pick<Providers.Livn.LineItem, "type" | "grossTotal" | "salesComputationDetails">,
  markupPerc?: number
): LivnPrice => {
  if (MARKUP_EXEMPT_LINE_TYPES.has(lineItem.type)) return lineItem.grossTotal;
  const isNetRate = lineItem.salesComputationDetails?.resSuppliedPriceIsNetRate === true;
  return isNetRate ? applyLivnMarkup(lineItem.grossTotal, markupPerc) : lineItem.grossTotal;
};

export type LivnInvoiceView = {
  lines: { title: string; quantity: number; displayed: LivnPrice }[];
  total: number;
  currency: string;
};

/**
 * The one derivation of a booking invoice for display — used by the confirmation screen,
 * the My Bookings modal and the ticket PDF, so the three can never disagree on the
 * certification-critical pricing policy.
 *
 * Returns undefined when the breakdown cannot be shown truthfully:
 * - a net-rate line needs the markup it was charged with, and reservations stored before
 *   markupPerc was persisted no longer know it (markup-exempt lines are shown at gross
 *   regardless, so they don't force this);
 * - mixed currencies across lines cannot be summed into one total.
 */
export const displayableLivnInvoice = (
  invoice: { lineItems: Providers.Livn.LineItem[]; grossTotal: LivnPrice } | undefined,
  markupPerc?: number
): LivnInvoiceView | undefined => {
  const items = invoice?.lineItems ?? [];
  if (items.length === 0) return undefined;

  const needsMarkup = items.some(
    i => !MARKUP_EXEMPT_LINE_TYPES.has(i.type) && i.salesComputationDetails?.resSuppliedPriceIsNetRate === true
  );
  if (needsMarkup && markupPerc === undefined) return undefined;

  const lines = items.map(item => ({
    title: item.title,
    quantity: item.quantity,
    displayed: displayLivnLineItemTotal(item, markupPerc),
  }));
  const currency = lines[0].displayed.currency ?? invoice?.grossTotal.currency ?? "";
  if (lines.some(l => l.displayed.currency !== currency)) return undefined;

  const total = Math.round(lines.reduce((sum, l) => sum + l.displayed.amount, 0) * 100) / 100;
  return { lines, total, currency };
};
